// src/prototype/data/decisionText.ts
// Pure parser for DecisionBrief.decisionText, used by the Brief tab's "Why we recommend this"
// card. Authored overrides read "Recommendation: <sentence>. Rationale: 1. … 2. … 3. …";
// derived briefs are plain prose. Text is never dropped: whatever does not parse as a
// numbered rationale stays in `body`.

export interface ParsedDecision {
  /** The recommendation sentence, any leading "Recommendation:" label removed. */
  recommendation: string;
  /** Numbered rationale points after "Rationale:", or null when there are none. */
  points: string[] | null;
  /** Remaining prose when the text has no numbered points; null when points were found. */
  body: string | null;
}

const LABEL = /^\s*Recommendation\s*[:\-–—]\s*/i;

/** Split "1. a 2. b 3. c" on sequential markers only, so "$1.2M" or "v2. " never splits. */
function splitNumbered(text: string): string[] | null {
  const points: string[] = [];
  let rest = text.trim();
  let n = 1;
  if (!rest.startsWith('1. ')) return null;
  rest = rest.slice(3);
  for (;;) {
    const next = rest.search(new RegExp(`\\s${n + 1}\\.\\s`));
    if (next === -1) {
      points.push(rest.trim());
      break;
    }
    points.push(rest.slice(0, next).trim());
    rest = rest.slice(next).replace(new RegExp(`^\\s${n + 1}\\.\\s`), '');
    n += 1;
  }
  return points.filter(Boolean).length > 0 ? points.filter(Boolean) : null;
}

export function parseDecisionText(raw: string): ParsedDecision {
  const text = raw.replace(LABEL, '').trim();
  const m = text.match(/\bRationale:\s*/i);
  if (!m || m.index === undefined) return { recommendation: text, points: null, body: null };

  const recommendation = text.slice(0, m.index).trim();
  const after = text.slice(m.index + m[0].length).trim();
  const points = splitNumbered(after);
  if (points) return { recommendation, points, body: null };
  // "Rationale:" without numbered points: keep every word.
  return { recommendation, points: null, body: after || null };
}
